import React, { useState, useEffect } from 'react';
import { certificatesAPI, BASE_SERVER_URL } from '../utils/api';
import { CERTIFICATES, AWARDS } from '../data/certificatesData';
import { Award, ShieldCheck, FileText, ExternalLink, Download } from 'lucide-react';
import { BROCHURE_PDF } from '../data/siteContent';

const CertCard = ({ title, issuer, detail, image, href }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="group flex flex-col overflow-hidden rounded-large border border-neutral-100 bg-white shadow-custom transition-all duration-300 hover:shadow-custom-hover"
  >
    <div className="relative flex h-72 items-center justify-center overflow-hidden border-b border-neutral-100 bg-background-100 p-4">
      <img
        src={image}
        alt={title}
        className="relative z-20 max-h-full max-w-full object-contain drop-shadow-md transition-transform duration-500 group-hover:scale-105"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = 'https://placehold.co/400x600?text=No+Image';
        }}
      />
    </div>
    <div className="flex flex-1 flex-col p-6 text-center">
      <h3 className="font-heading text-lg font-bold uppercase tracking-wide text-neutral-900 transition-colors group-hover:text-primary-600">
        {title}
      </h3>
      {issuer && (
        <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-brand-700">{issuer}</p>
      )}
      {detail && (
        <p className="mt-2 font-body text-sm leading-relaxed text-neutral-500">{detail}</p>
      )}
      <span className="mt-4 inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-widest text-primary-600">
        View document
        <ExternalLink className="h-3.5 w-3.5" />
      </span>
      <div className="mx-auto mt-4 h-1 w-12 rounded-full bg-gradient-to-r from-primary-600 to-secondary-500" />
    </div>
  </a>
);

const Certifications = () => {
  const [apiCertificates, setApiCertificates] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    certificatesAPI
      .getAll()
      .then((response) => {
        const extra = Array.isArray(response.data) ? response.data : [];
        setApiCertificates(extra);
      })
      .catch(() => {
        setApiCertificates([]);
      });
  }, []);

  return (
    <div className="certifications-page">
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-600 to-primary-900 py-20">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="container relative z-10 mx-auto px-6">
          <div className="mx-auto max-w-4xl text-center">
            <div className="mb-6 inline-block rounded-small border border-secondary-400/40 bg-secondary-500/20 px-6 py-2 backdrop-blur-sm">
              <span className="font-heading flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider text-secondary-300">
                <Award className="h-4 w-4" />
                Quality Guaranteed
              </span>
            </div>
            <h1 className="font-heading mb-6 text-5xl font-bold uppercase tracking-wide text-white md:text-6xl">
              Certifications <span className="text-secondary-400">& Awards</span>
            </h1>
            <p className="font-body mx-auto max-w-2xl text-xl leading-relaxed text-neutral-200">
              ISO and HACCP certifications, independent laboratory test reports, and industry awards for our flooring and coating work.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-20">
        <div className="container mx-auto px-6">
          <div className="mb-10 text-center">
            <div className="mb-3 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-brand-700">
              <ShieldCheck className="h-4 w-4" />
              Certifications
            </div>
            <h2 className="font-heading text-3xl font-bold text-neutral-900">Quality & test certificates</h2>
            <p className="mx-auto mt-2 max-w-2xl text-sm text-neutral-600">
              Management-system certificates and third-party test reports for SV Bond systems.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {CERTIFICATES.map((cert) => (
              <CertCard
                key={cert.id}
                title={cert.title}
                issuer={cert.issuer}
                detail={cert.detail}
                image={cert.image}
                href={cert.file}
              />
            ))}
            {apiCertificates.map((cert) => (
              <CertCard
                key={cert._id}
                title={cert.title}
                image={`${BASE_SERVER_URL}${cert.image}`}
                href={`${BASE_SERVER_URL}${cert.image}`}
              />
            ))}
          </div>

          <div className="mb-10 mt-20 text-center">
            <div className="mb-3 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-brand-700">
              <FileText className="h-4 w-4" />
              Awards
            </div>
            <h2 className="font-heading text-3xl font-bold text-neutral-900">Awards & recognitions</h2>
            <p className="mx-auto mt-2 max-w-2xl text-sm text-neutral-600">
              Merit awards and authorised-applicator recognition from ELANTAS Beck / Beck India.
            </p>
          </div>
          <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 md:grid-cols-2">
            {AWARDS.map((award) => (
              <CertCard
                key={award.id}
                title={award.title}
                issuer={award.issuer}
                detail={award.detail}
                image={award.image}
                href={award.file}
              />
            ))}
          </div>

          <div className="mt-16 text-center">
            <a
              href={BROCHURE_PDF}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-small bg-brand-900 px-6 py-3 text-sm font-bold text-white shadow-custom transition hover:bg-brand-800"
            >
              <Download className="h-4 w-4" />
              Download company brochure
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Certifications;
